// Appels HTTP + sessions sauvegardées (localStorage, lectures/écritures protégées par try/catch).
import type { TonePreset } from "../shared/types";
import type { ConteurKind } from "../shared/chatgpt";

export interface Session { code: string; worldName: string; playerId: string; token: string; playerName: string }

const SESSIONS_KEY = "veillee.sessions";
const LAST_KEY = "veillee.last";
const SESSION_KEYS = ["code", "worldName", "playerId", "token", "playerName"] as const;

const MSG_CREATE = "Impossible de créer le monde pour le moment. Réessaie dans un instant.";
const MSG_JOIN = "Impossible de rejoindre ce monde. Vérifie le code d’invitation et réessaie.";
const MSG_RESUME = "Impossible de retrouver ton héros. Vérifie le code du monde et ton code de retour.";
const MSG_SERVER = "Le serveur n’a pas répondu comme prévu. Réessaie dans un instant.";
const MSG_NETWORK = "Impossible de joindre le serveur. Vérifie ta connexion internet et réessaie.";
const MSG_RESPONSE = "Réponse inattendue du serveur. Réessaie dans un instant.";

// ───────── stockage local ─────────

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // stockage indisponible (navigation privée) : la session reste utilisable pour cette visite
  }
}

function isSession(x: unknown): x is Session {
  if (typeof x !== "object" || x === null) return false;
  return SESSION_KEYS.every((k) => {
    const v: unknown = (x as Record<string, unknown>)[k];
    return typeof v === "string" && v.length > 0;
  });
}

function readSessions(): Session[] {
  const raw = readStorage(SESSIONS_KEY);
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    return Array.isArray(data) ? data.filter(isSession) : [];
  } catch {
    return [];
  }
}

export function listSessions(): Session[] {
  return readSessions();
}

/** Ajoute ou met à jour une session ; la plus récente est en tête de liste. */
export function saveSession(s: Session): void {
  const entry: Session = { code: s.code, worldName: s.worldName, playerId: s.playerId, token: s.token, playerName: s.playerName };
  const others = readSessions().filter((x) => x.code !== s.code);
  writeStorage(SESSIONS_KEY, JSON.stringify([entry, ...others]));
}

export function removeSession(code: string): void {
  writeStorage(SESSIONS_KEY, JSON.stringify(readSessions().filter((x) => x.code !== code)));
  if (lastSessionCode() === code) setLastSessionCode(null);
}

export function lastSessionCode(): string | null {
  return readStorage(LAST_KEY) || null;
}

export function setLastSessionCode(code: string | null): void {
  writeStorage(LAST_KEY, code || null);
}

/** Session de la dernière visite, si elle est encore sauvegardée (à reprendre au lancement). */
export function lastSession(): Session | null {
  const code = lastSessionCode();
  if (!code) return null;
  return readSessions().find((s) => s.code === code) ?? null;
}

// ───────── HTTP ─────────

/** Message d'erreur renvoyé par le serveur (`{ error }`), s'il y en a un. */
function errorOf(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("error" in data)) return null;
  const message: unknown = data.error;
  return typeof message === "string" && message.trim() !== "" ? message : null;
}

async function postJson(path: string, body: unknown, fallback: string): Promise<unknown> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(MSG_NETWORK);
  }
  const data: unknown = await res.json().catch(() => null);
  const message = errorOf(data);
  if (message) throw new Error(message);
  if (!res.ok) throw new Error(res.status >= 500 ? MSG_SERVER : fallback);
  return data;
}

function toSession(data: unknown): Session {
  if (!isSession(data)) throw new Error(MSG_RESPONSE);
  return { code: data.code, worldName: data.worldName, playerId: data.playerId, token: data.token, playerName: data.playerName };
}

function remember(s: Session): Session {
  saveSession(s);
  return s;
}

/** Code tapé à la main : espaces ignorés, majuscules. */
function normalizeCode(code: string): string {
  return code.replace(/\s+/g, "").toUpperCase();
}

/** POST /api/worlds → Session (sauvegardée). Lève une Error au message FR si échec. */
export async function createWorld(args: { worldName: string; pitch: string; tone: TonePreset; playerName: string; expectedPlayers: number; conteur?: ConteurKind }): Promise<Session> {
  const data = await postJson("/api/worlds", {
    worldName: args.worldName.trim(),
    pitch: args.pitch.trim(),
    tone: args.tone,
    playerName: args.playerName.trim(),
    expectedPlayers: args.expectedPlayers,
    ...(args.conteur === "chatgpt" ? { conteur: "chatgpt" } : {}),
  }, MSG_CREATE);
  return remember(toSession(data));
}

/** POST /api/join → Session (sauvegardée). */
export async function joinWorld(code: string, playerName: string): Promise<Session> {
  const data = await postJson("/api/join", { code: normalizeCode(code), playerName: playerName.trim() }, MSG_JOIN);
  return remember(toSession(data));
}

/** POST /api/resume → Session du joueur dont c'est le code de retour (sauvegardée). */
export async function resumeWorld(code: string, recovery: string): Promise<Session> {
  const data = await postJson("/api/resume", { code: normalizeCode(code), recovery }, MSG_RESUME);
  return remember(toSession(data));
}

/**
 * GET /api/health : quels mondes peut-on créer ? Avec Claude (offert par Veillée), ou avec son propre ChatGPT.
 * Tout fermé si le serveur ne répond pas (il refuserait de toute façon).
 */
export async function creationRights(): Promise<Record<ConteurKind, boolean>> {
  try {
    const data = (await (await fetch("/api/health")).json()) as { creation?: unknown; creationChatgpt?: unknown } | null;
    return { claude: data?.creation === true, chatgpt: data?.creationChatgpt === true };
  } catch {
    return { claude: false, chatgpt: false };
  }
}

/** Lien d'invitation à partager : `${location.origin}/?code=XXXXXX` */
export function inviteLink(code: string): string {
  return `${location.origin}/?code=${encodeURIComponent(code)}`;
}
